import { IPromise, IHttpService, IFilterService } from "angular";
import dayjs from "@gtmhub/dayjs";

import { IEmployee } from "@gtmhub/employees/models/models";
import { IReport } from "@gtmhub/reporting/models";
import { IPlanningSession } from "@gtmhub/sessions/models/models";
import { ICollection } from "@gtmhub/models";
import { EnvironmentService } from "@gtmhub/env";
import { IGoal } from "@gtmhub/goals/models";
import { IHubSessionIdWorkflowMap } from "@gtmhub/hub/models/hub";
import { IMetric } from "@gtmhub/okrs/metrics";
import { IReflection, IReflectionUpdate } from "@webapp/reflections/models/reflections.models";
import { toKeyMap } from "../../util";

export interface IAssigneeStats {
  assigneeId: string;
  progress: number;
  attainmentDelta: ISessionDelta;
}

export interface ISessionProgress {
  assigneesStats: IAssigneeStats[];
  sessionId: string;
  sessionProgress: number;
  sessionAttainmentDelta: ISessionDelta;
}

export interface ISessionDelta {
  current: number;
  delta: number;
  deltaPercentage: number;
  previous: number;
}

export interface IHomeMetric extends IMetric {
  cadanceDeadline: string;
}

export interface ISessionsMap {
  [sessionId: string]: IPlanningSession;
}

export class HomeService {
  public static $inject = ["$http", "EnvironmentService", "$filter"];

  constructor(private $http: IHttpService, private env: EnvironmentService, private $filter: IFilterService) {}

  private strings = {
    deadlines: { overdue: "overdue" },
  };

  public cachedReports: IReport[] = null;

  public getGoalsToApprove(goalsToReview: IHubSessionIdWorkflowMap, allSessions: IPlanningSession[]): IGoal[] {
    const activeFutureSessions = allSessions.filter((s) => s.status !== "closed" && dayjs().isSameOrBefore(s.end));

    return Object.keys(goalsToReview).reduce((acc, sessionId) => {
      const isActiveFutureSession = activeFutureSessions.some((s) => s.id === sessionId);

      const notApprovedGoals = goalsToReview[sessionId].goalsToReview
        .filter((goal) => !goal.currentUserApproved && isActiveFutureSession)
        .map((goal) => ({ ...goal, deadline: activeFutureSessions.find((s) => s.id === sessionId).start, sessionId }));

      return acc.concat(notApprovedGoals);
    }, []);
  }

  public getSessionsProgress(activeSessionIds: string, employeeIdAndTeamIds: string): IPromise<ICollection<ISessionProgress>> {
    const url = this.env.getApiEndpoint(`/sessions/${activeSessionIds}/assignees/${employeeIdAndTeamIds}/stats`);

    return this.$http.get<ICollection<ISessionProgress>>(url).then((response) => response.data);
  }

  public calcDeadline = (date: string): string => (date ? dayjs(date).fromNow() : this.strings.deadlines.overdue);

  public getNotUpdatedMetricsInCurrentCadence = (metrics: IHomeMetric[], sessionsMap: ISessionsMap, employee: IEmployee): IHomeMetric[] => {
    const employeeIdAndManagedTeamIds = employee.managedTeamIds.concat(employee.id);
    // todo-items start to show at least on Wednesday, so if you need to debug/test something, increment the date or hard-code it to at least Wednesday
    // in-order to see your items
    const today = dayjs();

    // user can be a part of many teams, currently we show only todo-metrics where user is owner, or the metric belongs to a team he is a manager of
    return (
      metrics
        .filter((metric) => {
          if (sessionsMap[metric.sessionId] && sessionsMap[metric.sessionId].cadence) {
            const currentCadance = sessionsMap[metric.sessionId].cadence.ranges.find((range) => this.isBetweenDates(range.start, range.end, today));

            if (currentCadance) {
              const isNotUpdatedInCadance = !this.isBetweenDates(currentCadance.start, currentCadance.end, metric.lastCheckInDate);
              const isNearDeadline = this.isCloseDeadline(today, currentCadance.end);

              if (isNotUpdatedInCadance && isNearDeadline) {
                metric.cadanceDeadline = currentCadance.end;
              }
              return isNotUpdatedInCadance && isNearDeadline;
            }
          }

          return false;
        })
        // due to optimisation, we get all metrics for the userId,userTeamId,userManagedTeamId to reduce http calls
        // in todo-list we show only userId,userManagedTeamId so thats why we filter them here
        .filter((metric) => metric.ownerIds.some((ownerId) => employeeIdAndManagedTeamIds.includes(ownerId)))
        // metrics with no deadline dueDate have default one "0001-01-01T00:00:00Z"
        // metrics with hard deadline which is due by today aren't shown in todo-section
        .filter((metric) => (metric.dueDate === "0001-01-01T00:00:00Z" ? true : today.isSameOrBefore(metric.dueDate)))
        // updated boolean metrics aren't shown in todo-list
        .filter((metric) => (metric.manualType === "boolean" ? (metric.attainment === 0 ? true : false) : true))
    );
  };

  public getReflectionsNotUpdatedInCurrentCadence = (reflections: IReflection[], reflectionUpdates: IReflectionUpdate[]) => {
    const reflectionUpdatesMap = toKeyMap("reflectionId", reflectionUpdates);
    return reflections.filter((reflection) => !reflectionUpdatesMap[reflection.id]);
  };

  private isCloseDeadline = (today: dayjs.Dayjs, target: string) => {
    const dayOfWeek = dayjs(target).utc().day();

    const isSunday = dayOfWeek === 0;
    const isSaturday = dayOfWeek === 6;

    const daysShowing = isSaturday ? 3 : isSunday ? 4 : 2;

    return dayjs(target).diff(today, "days") <= daysShowing;
  };

  public getActiveSessionsMap(sessions: IPlanningSession[]): ISessionsMap {
    return sessions.reduce((agg, session) => {
      agg[session.id] = session;
      return agg;
    }, {} as ISessionsMap);
  }

  private isBetweenDates = (from: string, to: string, dateToCompare: string | dayjs.Dayjs): boolean => {
    return dateToCompare ? dayjs(dateToCompare).isBetween(from, to, null, "()") : false;
  };

  // employeeTeamIds,employeeManagedTeamIds
  public getEmployeeTeamIds(employee: IEmployee): string[] {
    return [employee.id].concat(employee.managedTeamIds, employee.teamIds).filter((teamId, index, self) => self.indexOf(teamId) === index);
  }

  public roundProgressValue = (value: number): number => {
    return +this.$filter("number")(value * 100, 0) / 100;
  };
}
